import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { getShopList, resolveShopPrimaryUrls, Shop, ShopListRequest } from '../../utils/shop';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { 
  Menu, 
  Bell, 
  User, 
  Users, 
  CreditCard,
  Coffee,
  Gift,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Package,
  Home,
  Search,
  Filter,
  MapPin,
  Clock,
  Phone,
  Globe,
  MoreVertical,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  ChevronUp,
  ArrowUpDown,
  Grid,
  List,
  Map,
  ExternalLink,
  X
} from 'lucide-react';

type ShopWithThumbnail = Shop & { thumbnailUrl?: string | null };


const ShopList: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('shop-list');

  // List state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [currentPage, setCurrentPage] = useState(0); // API는 0부터 시작
  const [pageSize, setPageSize] = useState(20);

  // URL 파라미터에서 페이징 정보 읽기
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    const size = searchParams.get('size');
    
    if (page !== null) {
      setCurrentPage(parseInt(page, 10));
    }
    if (size !== null) {
      setPageSize(parseInt(size, 10));
    }
  }, [location.search]);

  // URL 파라미터에서 뷰 타입 읽기
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const view = urlParams.get('view');
    if (view && ['list', 'grid', 'map'].includes(view)) {
      setViewType(view);
    }
  }, [location.search]);

  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedShops, setSelectedShops] = useState<string[]>([]);
  const [viewType, setViewType] = useState('list');
  const [hoveredShop, setHoveredShop] = useState<string | null>(null);

  // API state
  const [shops, setShops] = useState<ShopWithThumbnail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Check if screen is mobile size
  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }
      
      try {
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          return;
        }
        
        const response = await getBrandListFlat(projectCode);
        if (response && response.brands) {
          setBrands(response.brands);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        // 브랜드 목록 조회 실패해도 매장 목록은 조회 가능하도록 에러를 무시
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  // 검색어 Debounce 처리 (500ms 지연, 최소 2글자 이상)
  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    
    // 1글자 이하일 때는 즉시 빈 문자열로 설정
    if (trimmedSearchTerm.length <= 1) {
      setDebouncedSearchTerm('');
      return;
    }
    
    // 2글자 이상일 때는 500ms 후에 debounce 처리
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // 매장목록 조회
  useEffect(() => {
    const fetchShops = async () => {
      if (!projectDetail) {
        console.log('프로젝트 정보가 없습니다.');
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }
        
        const request: ShopListRequest = {
          page: currentPage,
          size: pageSize,
          sort: `${sortField},${sortOrder}`,
          keyword: debouncedSearchTerm || undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          brandCode: selectedBrand !== 'all' ? selectedBrand : undefined
        };
        
        console.log('매장목록 API 호출:', { projectCode, request });
        const response = await getShopList(projectCode, request);
        console.log('매장목록 API 응답:', response);
        
        if (response && response.shops) {
          const thumbnailUrlList = await resolveShopPrimaryUrls(projectCode, response.shops).catch(
            (err) => {
              console.warn('매장 썸네일 pressign-download 실패:', err);
              return response.shops.map(() => null);
            }
          );

          const shopsWithThumbnails: ShopWithThumbnail[] = response.shops.map((shop, i) => ({
            ...shop,
            thumbnailUrl: thumbnailUrlList[i] ?? null,
          }));

          setShops(shopsWithThumbnails);
          setTotalCount(response.totalCount);
          // API 응답의 pageSize에 맞게 pageSize 업데이트
          if (response.pageSize && response.pageSize !== pageSize) {
            setPageSize(response.pageSize);
          }
          console.log('설정된 매장 목록:', shopsWithThumbnails);
          console.log('설정된 총 개수:', response.totalCount);
          console.log('설정된 페이지 크기:', response.pageSize);
        } else {
          console.warn('응답에 shops 데이터가 없습니다:', response);
          setShops([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('매장목록 조회 오류:', err);
        setError(err instanceof Error ? err.message : '매장목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    if (projectDetail) {
      fetchShops();
    }
  }, [projectDetail, currentPage, pageSize, sortField, sortOrder, debouncedSearchTerm, selectedStatus, selectedBrand]);

  // Helper functions
  const getStatusBadge = (status: string) => {
    const badgeInfo = ShopFormUtils.getStatusBadge(status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeInfo.color}`}>
        <span className="w-1.5 h-1.5 mr-1 bg-current rounded-full"></span>
        {badgeInfo.text}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeInfo = ShopFormUtils.getTypeBadge(type);
    return (
      <span className={typeInfo.className}>
        {typeInfo.label}
      </span>
    );
  };

  // 날짜 포맷팅 함수 (년월일만 표시)
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return ShopFormUtils.formatDate(dateString);
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return dateString.split(' ')[0]; // 기존 방식으로 fallback
    }
  };

  // Sort function
  const handleSort = (field: string) => {
    ShopFormUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  };

  // Select all handler
  const handleSelectAll = () => {
    ShopFormUtils.handleSelectAll(selectedShops, filteredShops, setSelectedShops);
  };

  // Select shop handler
  const handleSelectShop = (shopCode: string) => {
    ShopFormUtils.handleSelectShop(shopCode, selectedShops, setSelectedShops);
  };

  // Page change handler
  const handlePageChange = (page: number) => {
    ShopFormUtils.handlePageChange(page, setCurrentPage);
  };

  // Page size change handler
  const handlePageSizeChange = (size: number) => {
    ShopFormUtils.handlePageSizeChange(size, setPageSize, setCurrentPage);
  };

  // 검색 조건 변경 시 페이지 리셋
  useEffect(() => {
    if (projectDetail) {
      setCurrentPage(0);
    }
  }, [debouncedSearchTerm, selectedStatus, selectedBrand]);

  // 서버 사이드 필터링을 사용하므로 클라이언트 필터링 제거
  const filteredShops: ShopWithThumbnail[] = shops;

  const renderEmptyShopList = () => (
    <div className="flex flex-col items-center py-12">
      <Home className="w-12 h-12 text-gray-400 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">매장이 없습니다</h3>
      <p className="text-gray-500 mb-4">새로운 매장을 생성해보세요</p>
      <button
        onClick={() => ShopFormUtils.handleShopCreate(navigate)}
        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-4 h-4" />
        매장 생성
      </button>
    </div>
  );

  // 프로젝트 정보가 없거나 에러가 있을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          {error ? (
            <>
              <div className="text-red-500 mb-4">
                <Home className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-red-600 mb-4 text-center max-w-md">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                새로고침
              </button>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="매장 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* Shop List Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  {/* Search - 최소폭 보장으로 입력값 가려짐 방지 */}
                  <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="매장명, 매장코드, 주소로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="검색어 삭제"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Status Filter */}
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 상태</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="PENDING">대기중</option>
                    <option value="TERMINATED">종료</option>
                    <option value="DELETED">삭제</option>
                  </select>

                  {/* Brand Filter */}
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 브랜드</option>
                    {brands.map((brand) => (
                      <option key={brand.code} value={brand.code}>
                        {brand.name}
                      </option>
                    ))}
                  </select>

                  {/* Group Filter */}
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 그룹</option>
                    <option value="group-001">서울 지역</option>
                    <option value="group-002">경기 지역</option>
                    <option value="group-003">인천 지역</option>
                    <option value="group-004">부산 지역</option>
                    <option value="group-005">대구 지역</option>
                    <option value="group-006">대전 지역</option>
                    <option value="group-007">광주 지역</option>
                    <option value="group-008">울산 지역</option>
                    <option value="group-009">강원 지역</option>
                    <option value="group-010">충청 지역</option>
                    <option value="group-011">전라 지역</option>
                    <option value="group-012">경상 지역</option>
                  </select>

                  {/* View Type Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => ShopFormUtils.handleViewTypeChange('list', setViewType, location, navigate)}
                      className={`p-1.5 rounded ${viewType === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      title="목록 보기"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => ShopFormUtils.handleViewTypeChange('grid', setViewType, location, navigate)}
                      className={`p-1.5 rounded ${viewType === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      title="그리드 보기"
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        alert('지도 보기는 준비중입니다.');
                      }}
                      className="p-1.5 rounded hover:bg-gray-200 opacity-50 cursor-not-allowed"
                      title="지도 보기 (준비중)"
                    >
                      <Map className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Excel Download */}
                  <button
                    onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center">
                    <Download className="w-4 h-4 mr-2" />
                    엑셀
                  </button>

                  {/* Add Shop Button */}
                  <button 
                    onClick={() => ShopFormUtils.handleShopCreate(navigate)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    매장 추가
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  검색결과 <span className="font-medium text-gray-900">{totalCount}</span>개 매장
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    활성: {shops.filter(s => s.status === 'ACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                    비활성: {shops.filter(s => s.status === 'INACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-1"></span>
                    대기중: {shops.filter(s => s.status === 'PENDING').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-400 rounded-full mr-1"></span>
                    종료: {shops.filter(s => s.status === 'TERMINATED').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-red-500 mb-4">
                  <Home className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            )}

            {/* Table View - 에러가 없을 때만 표시 */}
            {!error && viewType === 'list' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="pl-4 pr-0 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedShops.length === filteredShops.length && filteredShops.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '80px', width: '80px' }}>
                        <button
                          onClick={() => handleSort('number')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                        >
                          번호
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <button
                          onClick={() => handleSort('outletCode')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          매장 코드
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '80px', width: '80px' }}>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          이미지
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '200px' }}>
                        <button
                          onClick={() => handleSort('name')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          매장명
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          타입
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left hidden lg:table-cell">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          주소/영업시간
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left hidden lg:table-cell">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          전화번호
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <button
                          onClick={() => handleSort('createdAt')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          생성일
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">작업</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={11} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredShops.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-6 py-12 text-center">
                          {renderEmptyShopList()}
                        </td>
                      </tr>
                    ) : (
                      filteredShops.map((shop) => (
                      <tr key={shop.code} className="hover:bg-gray-50">
                         <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedShops.includes(shop.code)}
                            onChange={(e) => {
                              handleSelectShop(shop.code);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ minWidth: '80px', width: '80px' }}>
                          #{shop.number}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {shop.outletCode}
                        </td>
                        <td className="pl-6 pr-0 py-4" style={{ minWidth: '80px', width: '80px' }}>
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {shop.thumbnailUrl ? (
                              <img
                                src={shop.thumbnailUrl}
                                alt={`${shop.name} 썸네일`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling;
                                  if (fallback instanceof HTMLElement) {
                                    fallback.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-full h-full items-center justify-center ${
                                shop.thumbnailUrl ? 'hidden' : 'flex'
                              }`}
                            >
                              <Home className="w-7 h-7 text-gray-400" />
                            </div>
                          </div>
                        </td>
                         <td className="pl-6 pr-0 py-4" style={{ minWidth: '200px' }}>
                           <div>
                             <div className="text-sm font-medium text-gray-900 truncate">
                               {shop.brandName ? `[${shop.brandName}] ${shop.name}` : shop.name}
                             </div>
                             {shop.comment && (
                               <div className="text-xs text-gray-400 mt-1 truncate">{shop.comment}</div>
                             )}
                           </div>
                         </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                          {getTypeBadge(shop.type)}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                          {getStatusBadge(shop.status)}
                        </td>
                        <td className="pl-6 pr-0 py-4 hidden lg:table-cell">
                          <div className="text-sm text-gray-900 max-w-xs">
                            <div className="flex items-start">
                              <MapPin className="w-4 h-4 text-gray-400 mr-1 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="truncate">
                                  {shop.addressRoad}
                                  {shop.addressDetail && ` ${shop.addressDetail}`}
                                </div>
                              </div>
                            </div>
                            {shop.salesTime && (
                              <div className="flex items-start mt-1">
                                <Clock className="w-4 h-4 text-gray-400 mr-1 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-gray-900 truncate">
                                  {shop.salesTime}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap hidden lg:table-cell">
                          <div className="text-sm text-gray-500">
                            <Phone className="inline w-4 h-4 mr-1" />
                            {shop.phoneNumber}
                          </div>
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(shop.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {shop.homepage && (
                              <button
                                className="text-gray-400 hover:text-blue-600"
                                title="홈페이지 보기"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              className="text-gray-400 hover:text-gray-600"
                              title="상세 보기"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              className="text-gray-400 hover:text-blue-600"
                              title="수정"
                              onClick={() => ShopFormUtils.handleShopEdit(navigate, shop.code, currentPage, pageSize)}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="text-gray-400 hover:text-red-600"
                              title="삭제"
                              onClick={() => ShopFormUtils.handleShopDelete(shop.code)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Selected Actions Bar */}
              {selectedShops.length > 0 && (
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      {selectedShops.length}개 매장 선택됨
                    </span>
                    <div className="flex items-center space-x-2">
                      <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                        상태 변경
                      </button>
                      <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                        엑셀 다운로드
                      </button>
                      <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">
                        선택 삭제
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Grid View - 에러가 없을 때만 표시 */}
            {!error && viewType === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {isLoading ? (
                <div className="col-span-full flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : filteredShops.length === 0 ? (
                <div className="col-span-full">
                  {renderEmptyShopList()}
                </div>
              ) : (
                filteredShops.map((shop) => (
                <div
                  key={shop.code}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow relative"
                  onMouseEnter={() => setHoveredShop(shop.code)}
                  onMouseLeave={() => setHoveredShop(null)}
                >
                  {/* Shop Image */}
                  <div className="h-48 bg-gray-100 relative">
                    {shop.thumbnailUrl ? (
                      <img
                        src={shop.thumbnailUrl}
                        alt={`${shop.name} 썸네일`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback instanceof HTMLElement) {
                            fallback.classList.remove('hidden');
                            fallback.classList.add('flex');
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-full items-center justify-center ${
                        shop.thumbnailUrl ? 'hidden' : 'flex'
                      }`}
                    >
                      <Home className="w-12 h-12 text-gray-400" />
                    </div>
                    {/* Status Badge on Image */}
                    <div className="absolute top-3 left-3">
                      {getStatusBadge(shop.status)}
                    </div>
                    {/* Type Badge */}
                    <div className="absolute top-3 right-3">
                      {getTypeBadge(shop.type)}
                    </div>
                  </div>

                  {/* Shop Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {shop.brandName ? `[${shop.brandName}] ${shop.name}` : shop.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {shop.outletCode} • #{shop.number}
                        </p>
                      </div>
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>

                    {/* Comment */}
                    {shop.comment && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {shop.comment}
                      </p>
                    )}

                    {/* Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 line-clamp-1">{shop.addressRoad}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-600 truncate">{shop.salesTime}</span>
                      </div>
                      {shop.phoneNumber && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-600">{shop.phoneNumber}</span>
                        </div>
                      )}
                    </div>

                    {/* Hover Actions */}
                    {hoveredShop === shop.code && (
                      <div className="absolute inset-x-0 bottom-0 bg-white border-t border-gray-200 p-3 flex justify-around">
                        <button className="flex items-center text-sm text-gray-600 hover:text-blue-600">
                          <Eye className="w-4 h-4 mr-1" />
                          상세
                        </button>
                        <button 
                          className="flex items-center text-sm text-gray-600 hover:text-blue-600"
                          onClick={() => ShopFormUtils.handleShopEdit(navigate, shop.code, currentPage, pageSize)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          수정
                        </button>
                        <button 
                          className="flex items-center text-sm text-gray-600 hover:text-red-600"
                          onClick={() => ShopFormUtils.handleShopDelete(shop.code)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          삭제
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
              )}
            </div>
            )}

            {/* Pagination */}
            {shops.length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">페이지당</span>
                  <select 
                    value={pageSize}
                    onChange={(e) => {
                      handlePageSizeChange(parseInt(e.target.value));
                    }}
                    className="px-1 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10개</option>
                    <option value="20">20개</option>
                    <option value="50">50개</option>
                    <option value="100">100개</option>
                  </select>
                  <span>표시</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(0)}
                    disabled={currentPage === 0}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    처음
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.ceil(totalCount / pageSize) }, (_, i) => i).slice(0, 5).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / pageSize) - 1, currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize) - 1)}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    마지막
                  </button>
                </div>

                <p className="text-sm text-gray-600">
                  {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} / 총 {totalCount}개
                </p>
              </div>
            </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ShopList;